import { useRef, useState, type ChangeEvent, type FormEvent, type KeyboardEvent } from 'react'
import { ArrowUp, FileText, X } from 'lucide-react'
import { Button } from '@/components/ds2'
import { showError, showWarning } from '@/utils/snackbar'
import { useGeneralAgentChatStore } from '../hooks/generalAgentChatStore'
import { ATTACH_ACCEPT, attachedMessage, attachRefusal, uploadAttachments } from './uploads/chatAttachments'
import { RevisionReview } from './uploads/RevisionReview'
import type { OpenedRevision } from './uploads/uploadFlow'

interface GeneralAgentComposerProps {
    onSend: (text: string) => void
}

/**
 * The chat's message box (v17): the attached files as chips (× removes one), the text area, "＋ Attach documents" and the round send
 * button. Enter sends, Shift+Enter breaks the line; an Enter that picks an IME candidate only finishes the word. On send the files go
 * through the board's own rate con upload first (`uploadAttachments`), then the message carries the typed words and what each upload
 * filed. A file that failed stays as a chip, with the board's words in a toast. A con filed as a revision is reviewed above the box.
 */
export const GeneralAgentComposer = ({ onSend }: GeneralAgentComposerProps) => {
    const [text, setText] = useState('')
    const [files, setFiles] = useState<File[]>([])
    const [uploading, setUploading] = useState(false)
    const [revisions, setRevisions] = useState<OpenedRevision[]>([])
    const picker = useRef<HTMLInputElement>(null)
    const notify = useGeneralAgentChatStore((s) => s.notify)
    const canSend = !uploading && (text.trim().length > 0 || files.length > 0)

    const onPicked = (event: ChangeEvent<HTMLInputElement>) => {
        const picked = [...(event.target.files ?? [])]
        event.target.value = ''
        if (!picked.length) return
        const refusal = attachRefusal(picked)
        if (refusal) return void showWarning(refusal)
        setFiles((current) => [...current, ...picked])
    }

    const sendWithFiles = async (typed: string, attached: File[]) => {
        setUploading(true)
        // An upload can take minutes: the message belongs to the chat that was open when the user pressed send, not the one open after.
        const sentFrom = useGeneralAgentChatStore.getState().tabId
        try {
            const outcome = await uploadAttachments(attached)
            setFiles(outcome.failed.map(({ file }) => file))
            if (outcome.failed.length) showError(outcome.failed.map(({ file, message }) => `${file.name}: ${message}`).join(' '))
            if (outcome.revisions.length) setRevisions((current) => [...current, ...outcome.revisions])
            // Nothing was filed: the typed words wait in the box with the files, so the user can retry or remove them.
            if (!outcome.lines.length) return
            if (useGeneralAgentChatStore.getState().tabId !== sentFrom) {
                // The files are filed already (attaching them again would upload them twice), so only the message is not sent.
                const failed = new Set(outcome.failed.map(({ file }) => file))
                const filed = attached.filter((file) => !failed.has(file)).map((file) => file.name)
                showWarning(
                    `${filed.join(', ')} ${filed.length === 1 ? 'was' : 'were'} uploaded, but another chat was opened before the upload finished, so no message was sent. There is no need to attach ${filed.length === 1 ? 'it' : 'them'} again.`
                )
                return
            }
            onSend(attachedMessage(typed, outcome.lines))
            // Words typed while the files uploaded stay in the box.
            setText((current) => (current === typed ? '' : current))
        } finally {
            setUploading(false)
        }
    }

    const submit = (event?: FormEvent) => {
        event?.preventDefault()
        if (!canSend) return
        if (files.length) return void sendWithFiles(text, files)
        onSend(text.trim())
        setText('')
    }

    const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) submit(event)
    }

    return (
        <div className="flex flex-col gap-2">
            {revisions.map((revision) => (
                <RevisionReview
                    key={revision.revisionId}
                    revision={revision}
                    onSaid={notify}
                    onDone={() => setRevisions((current) => current.filter((open) => open.revisionId !== revision.revisionId))}
                />
            ))}
            <form
                onSubmit={submit}
                className="flex flex-col gap-2 rounded-2xl border border-border bg-surface p-3 shadow-field transition-shadow duration-150 focus-within:shadow-none focus-within:ring-2 focus-within:ring-accent"
            >
                {files.length > 0 && (
                    <ul aria-label="Attached documents" className="flex flex-wrap gap-2">
                        {files.map((file, index) => (
                            <li key={`${file.name}-${index}`} className="flex items-center gap-1.5 rounded-lg bg-default px-2 py-1 text-xs text-foreground">
                                <FileText className="h-3.5 w-3.5 text-foreground-muted" aria-hidden />
                                <span className="max-w-[16rem] truncate">{file.name}</span>
                                <button
                                    type="button"
                                    aria-label={`Remove ${file.name}`}
                                    disabled={uploading}
                                    className="rounded text-foreground-muted hover:text-foreground disabled:opacity-50"
                                    onClick={() => setFiles((current) => current.filter((_, at) => at !== index))}
                                >
                                    <X className="h-3.5 w-3.5" aria-hidden />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
                <textarea
                    aria-label="Message invoicing agent"
                    rows={2}
                    value={text}
                    onChange={(event) => setText(event.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder="Message your invoicing agent…"
                    className="max-h-48 min-h-[3rem] w-full resize-none bg-transparent text-base text-foreground outline-none placeholder:text-foreground-muted"
                />
                <div className="flex items-center gap-2">
                    <Button type="button" variant="ghost" size="sm" disabled={uploading} onClick={() => picker.current?.click()}>
                        ＋ Attach documents
                    </Button>
                    <input ref={picker} type="file" multiple hidden accept={ATTACH_ACCEPT} aria-label="Attach documents" onChange={onPicked} />
                    {uploading && (
                        <span role="status" className="min-w-0 flex-1 truncate text-xs text-foreground-muted">
                            Reading the documents. This can take up to two minutes each.
                        </span>
                    )}
                    <Button type="submit" iconOnly aria-label="Send message" disabled={!canSend} loading={uploading} className="ml-auto">
                        <ArrowUp className="h-4 w-4" aria-hidden />
                    </Button>
                </div>
            </form>
        </div>
    )
}
